import {
  Button,
  Center,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
} from '@chakra-ui/react';
import { faClone, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import { useQuery } from 'urql';
import { useTranslation } from 'react-i18next';

import { useParams } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import { GetProjectSettingsDocument } from '../../../gql/graphql';
import SparkelIcon from '../../common/icon/SparkelIcon';
import CloneProjectTab from '../CloneProjectTab';
import CreateTemplateFromProjectTab from '../CreateTemplateFromProjectTab';
import DeleteProjectTab from '../DeleteProjectTab';
import ElementCategoriesTab from '../ElementCategories';
import ExportProjectTab from '../ExportProjectTab';
import OrderLabelsTab from '../OrderLabelsTab';
import AdvancedSettingsTab from '../AdvancedSettingsTab';
import GeneralSettingsTab from '../GeneralSettingsTab';
import UnitSettingsTab from '../UnitSettingsTab';
import CreditsAllocationTab from './CreditsAllocationTab';
import { useUserTenant } from 'src/services/auth-info';

type ProjectSettingsModalProps = {
  projectId: string;
  modalIsOpen: boolean;
  closeModal: () => void;
};

type ProjectPathParams = {
  projectId: string;
};

export default function ProjectSettingsModal({
  modalIsOpen,
  closeModal,
  projectId,
}: ProjectSettingsModalProps): React.ReactElement {
  const { t } = useTranslation('company');

  return (
    <Modal size={'4xl'} isOpen={modalIsOpen} onClose={closeModal}>
      <ModalOverlay></ModalOverlay>
      <ModalContent height={'60%'} minHeight={'fit-content'}>
        <ModalHeader paddingBottom={0}>
          {t('project-settings-modal.title')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {modalIsOpen ? (
            <ProjectSettings projectId={projectId} closeModal={closeModal} />
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// eslint-disable-next-line complexity
function ProjectSettings({
  projectId,
  closeModal,
}: {
  projectId: string;
  closeModal: () => void;
}) {
  const [{ data: getProjectSettings, fetching, error }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });

  const enableUnitSettings = useFlag('enable-unit-settings');
  const { t } = useTranslation('company');
  const { isAdmin, isFreeContractor, isStandardContractor } = useUserTenant();

  const params = useParams<keyof ProjectPathParams>() as ProjectPathParams;
  const isOnCompanyPage = params.projectId ? false : true;

  const project = getProjectSettings?.project;

  const redIconColor = useColorModeValue('red.500', 'red.200');
  const cyanIconColor = useColorModeValue('cyan.500', 'cyan.300');

  const dividerColor = useColorModeValue('gray.200', 'gray.700');

  if (fetching || !project) {
    return (
      <Center>
        <Spinner color="brand.400" />
      </Center>
    );
  }

  return (
    <>
      <Divider borderColor={dividerColor} marginBottom={2} />
      <Tabs
        orientation="vertical"
        variant="soft-rounded"
        colorScheme="blue"
        height={'100%'}
        size={'sm'}
      >
        <TabList width={300} marginRight={2}>
          <Tab justifyContent="left" borderRadius={'md'}>
            {t('project-settings-modal.tabs.general')}
          </Tab>
          {enableUnitSettings && (
            <Tab justifyContent="left" borderRadius={'md'}>
              {t('project-settings-modal.tabs.units')}
            </Tab>
          )}
          {!isFreeContractor && (
            <Tab justifyContent="left" borderRadius={'md'}>
              {t('project-settings-modal.tabs.attributes')}
            </Tab>
          )}
          {!isFreeContractor && !isStandardContractor && (
            <Tab justifyContent="left" borderRadius={'md'}>
              {t('project-settings-modal.tabs.table-labels')}
            </Tab>
          )}
          {!isFreeContractor && !isStandardContractor && (
            <Tab
              justifyContent="left"
              borderRadius={'md'}
              isDisabled={isOnCompanyPage}
            >
              {t('project-settings-modal.tabs.export-project')}
            </Tab>
          )}
          {!isFreeContractor && !isStandardContractor && (
            <Tab justifyContent="left" borderRadius={'md'}>
              {t('project-settings-modal.tabs.advanced')}
            </Tab>
          )}
          {isAdmin && !isFreeContractor && (
            <Tab justifyContent="left" borderRadius={'md'}>
              {t('project-settings-modal.tabs.credits')}
            </Tab>
          )}
          <Divider borderColor={dividerColor} marginY={2} />
          <Tab
            justifyContent="left"
            borderRadius={'md'}
            padding={0}
            _selected={{ backgroundColor: 'none' }}
          >
            <Button
              size={'sm'}
              justifyContent={'left'}
              borderRadius={'md'}
              leftIcon={
                <SparkelIcon size="xs" color={cyanIconColor} icon={faClone} />
              }
              colorScheme="cyan"
              variant={'ghost'}
              width={'100%'}
            >
              {t('project-settings-modal.actions.clone-project')}
            </Button>
          </Tab>
          {!isFreeContractor && (
            <Tab
              justifyContent="left"
              borderRadius={'md'}
              padding={0}
              _selected={{ backgroundColor: 'none' }}
            >
              <Button
                size={'sm'}
                justifyContent={'left'}
                borderRadius={'md'}
                leftIcon={
                  <SparkelIcon size="xs" color={cyanIconColor} icon={faPlus} />
                }
                colorScheme="cyan"
                variant={'ghost'}
                width={'100%'}
              >
                {t('project-settings-modal.actions.save-as-template')}
              </Button>
            </Tab>
          )}
          <Tab
            justifyContent="left"
            borderRadius={'md'}
            padding={0}
            _selected={{ backgroundColor: 'none' }}
          >
            <Button
              size={'sm'}
              justifyContent={'left'}
              borderRadius={'md'}
              leftIcon={
                <SparkelIcon size="xs" color={redIconColor} icon={faTrash} />
              }
              colorScheme="red"
              variant={'ghost'}
              width={'100%'}
            >
              {t('project-settings-modal.actions.delete-project')}
            </Button>
          </Tab>
        </TabList>
        <Divider
          borderColor={dividerColor}
          orientation="vertical"
          height={'100%'}
        />
        <TabPanels padding={4}>
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              {t('project-settings-modal.headings.general-settings')}
            </Heading>
            <GeneralSettingsTab project={project} />
          </TabPanel>
          {enableUnitSettings && (
            <TabPanel paddingTop={0}>
              <Heading marginBottom={3} size={'md'}>
                {t('project-settings-modal.headings.project-units')}
              </Heading>
              <UnitSettingsTab project={project} />
            </TabPanel>
          )}
          {!isFreeContractor && (
            <TabPanel paddingTop={0}>
              <ElementCategoriesTab project={project}></ElementCategoriesTab>
            </TabPanel>
          )}
          {!isFreeContractor && !isStandardContractor && (
            <TabPanel paddingTop={0}>
              <OrderLabelsTab project={project}></OrderLabelsTab>
            </TabPanel>
          )}
          {!isFreeContractor && !isStandardContractor && (
            <TabPanel paddingTop={0}>
              <Heading marginBottom={3} size={'md'}>
                {t('project-settings-modal.headings.export-project')}
              </Heading>
              <ExportProjectTab />
            </TabPanel>
          )}
          {!isFreeContractor && !isStandardContractor && (
            <TabPanel paddingTop={0}>
              <Heading marginBottom={3} size={'md'}>
                {t('project-settings-modal.headings.advanced')}
              </Heading>
              <AdvancedSettingsTab project={project} />
            </TabPanel>
          )}
          {isAdmin && !isFreeContractor && (
            <TabPanel paddingTop={0}>
              <Heading marginBottom={3} size={'md'}>
                {t('project-settings-modal.headings.credits')}
              </Heading>
              <CreditsAllocationTab
                projectId={projectId}
                projectName={project.name ?? ''}
              />
            </TabPanel>
          )}
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              {t('project-settings-modal.headings.clone-project')}
            </Heading>
            <CloneProjectTab
              project={project}
              closeModal={closeModal}
            ></CloneProjectTab>
          </TabPanel>
          {!isFreeContractor && (
            <TabPanel paddingTop={0}>
              <Heading marginBottom={3} size={'md'}>
                {t('project-settings-modal.headings.generate-template')}
              </Heading>
              <CreateTemplateFromProjectTab
                project={project}
              ></CreateTemplateFromProjectTab>
            </TabPanel>
          )}
          <TabPanel paddingTop={0}>
            <Heading marginBottom={3} size={'md'}>
              {t('project-settings-modal.headings.delete-project')}
            </Heading>
            <DeleteProjectTab
              project={project}
              closeModal={closeModal}
            ></DeleteProjectTab>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
}
